import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Grid } from 'antd';
import { CloseCircleOutlined, LeftOutlined } from '@ant-design/icons';
import get from 'lodash/get';

import MobileDrawer from '@design-system/components/MobileDrawer/MobileDrawer';
import MobileModal from '@design-system/components/MobileModal/MobileModal';
import CustomButton from '@source/components/CustomButton/CustomButton';
import Logger from '@source/services/Logger';

import useRegionConfig from '@source/hooks/useRegionConfig';
import useOTPService from '@source/hooks/useOTPService';
import useUserProfileServices from '@source/hooks/useUserProfileServices';
import useGTMEvent from '@source/hooks/useGTMEvent';
import type { IVerifyOTPResponseData } from '@source/interface/otp';
import { LOGIN_TYPE_DEFAULT, LOGIN_OTP_MAX_ATTEMPTS } from '@design-system/components/Login/constants';
import { LoginStepAnotherType, LoginStepOTP } from '@design-system/components/Login/steps';

const { useBreakpoint } = Grid;

interface IVerifyOTPContainer {
  onVerifyOTPSuccess: (token?: string, userId?: string) => void;
  visible: boolean;
  onModalClose: () => void;
  // eslint-disable-next-line react/require-default-props
  requestOtpType?: string;
  isZeekr?: boolean;
}

const StyledContentWrapper = styled.div`
  text-align: left;
  padding: 24px;

  @media screen and (min-width: 768px) {
    padding: 32px;
  }

  .header-title {
    ${(props) => props.theme.typo.style.lHeader}
  }

  .sub-header {
    ${(props) => props.theme.typo.style.body}
  }

  .error-message {
    color: ${(props) => props.theme.color.errorHighEmphasis};
  }

  .back-to-listing-btn {
    color: ${(props) => props.theme.color.linkHighEmphasis};
  }
`;

const StyledCustomHeader = styled.div`
  width: 100%;
  height: 65px;
  padding: 20px;
  ${(props) => props.theme.typo.familyGoogle.bold};
  border-bottom: 1px solid ${(props) => props.theme.color.onBackgroundLowEmphasis};
  display: grid;
  position: relative;
  top: 0;
  grid-template-columns: 1fr 10fr 1fr;
  place-items: center;
  background-color: ${(props) => props.theme.color.background};
  border-top-left-radius: 20px !important;
  border-top-right-radius: 20px !important;

  @media only screen and (min-width: 768px) {
    padding: 24px 24px 0 24px;
    border-bottom: 1px solid transparent;
  }

  .modal-title-text {
    grid-column-start: 2;

    &.drawer-title {
      font-size: 14px;
      line-height: 22px;
      font-weight: 600;
    }
  }
`;

const StyledCloseIcon = styled(CloseCircleOutlined)`
  font-size: 21px;
  cursor: pointer;
  margin-left: auto;

  &:hover {
    color: ${(props) => props.theme.color.primaryBase};
  }
`;

const StyledCustomButton = styled(CustomButton)`
  width: 100%;
  ${(props) => props.theme.typo.familyGoogle.semiBold};
  font-weight: 600;
  border: ${(props) => `1px solid ${props.theme.color.onBackgroundLowEmphasis}`};
  color: ${(props) => props.theme.color.onSurface};

  &:hover,
  &:focus {
    border: ${(props) => `1px solid ${props.theme.color.onBackgroundLowEmphasis}`};
    box-shadow: none;
    color: ${(props) => props.theme.color.onSurface};
  }
`;

const VerifyOTPContainer: React.FC<IVerifyOTPContainer> = ({
  requestOtpType,
  visible,
  onVerifyOTPSuccess,
  onModalClose,
  isZeekr,
}) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const { requestOTP, verifyOTP, otpCountdownDone, otpState, resetOTPState, setType, setPhone } = useOTPService();
  const {
    contactId,
    loading,
    type: otpType,
    phone: phoneOTPState,
    lockSeconds,
    countdownTimer,
    isCountdownOTP,
  } = otpState || {};
  // https://carrotech.atlassian.net/browse/CCV-1433
  // Add a temp option to skip redirect after lock countdown is done.
  // This is temp solution. Will refactor by treating the lock countdown as a login step instead of by state
  const [skipRedirectAfterLockCountdown, setSkipRedirectAfterLockCountdown] = useState(false);
  const { saveUserToCookie, userProfileState, fetchProfile } = useUserProfileServices();

  const [currentStep, setCurrentStep] = useState(0);
  const nextStep = () => setCurrentStep(currentStep < 2 ? currentStep + 1 : currentStep);
  const backStep = () => setCurrentStep(currentStep - 1);

  const { phone: userProfilePhone } = userProfileState?.profile || {};

  const {
    countryConfig: { country },
  } = useRegionConfig();

  const { pushGTMEvent } = useGTMEvent();

  // Set default login type "whatsapp" | "sms" for MY on first load modal
  useEffect(() => {
    if (visible) {
      if (!requestOtpType) {
        setType(get(LOGIN_TYPE_DEFAULT, 'my', 'sms'));
      }
      setSkipRedirectAfterLockCountdown(false);
    }
  }, [requestOtpType, visible]);

  // const onRequestOTP = async (values: IFormInputs | object) => {
  //   const payload = { ...values, type: otpType };

  //   // https://redux.js.org/style-guide/#put-as-much-logic-as-possible-in-reducers
  //   // I put whole logic into `otp` reducer @teh
  //   const res = await requestOTP({ payload });

  //   return res;
  // };

  const handleRequestOTP = async (type?: string) => {
    let res = null;

    try {
      // https://redux.js.org/style-guide/#put-as-much-logic-as-possible-in-reducers
      // I put whole logic into `otp` reducer @teh
      res = await requestOTP({
        payload: {
          phone: phoneOTPState || undefined,
          type,
          group_source: isZeekr ? 'zeekr' : undefined,
        },
      });
      setSkipRedirectAfterLockCountdown(false);
    } catch (error) {
      Logger.log(error);
    }

    return res;
  };

  const handleOnCountdownTimerEnd = () => {
    setSkipRedirectAfterLockCountdown(true);

    setTimeout(() => {
      otpCountdownDone();
    }, 300);
  };

  const handleResendOTP = (): Promise<unknown> => handleRequestOTP(otpType);

  const handleVerifyOTP = async ({ otpCode }: { otpCode: string }) => {
    try {
      const payload = {
        phone: phoneOTPState,
        otp_code: otpCode,
        isRedirectOnSuccess: false,
        contact_id: contactId,
        group_source: isZeekr ? 'zeekr' : undefined,
      };

      const verifyOTPResp = await verifyOTP({ country: country || 'sg', payload });
      const { data, success } = (verifyOTPResp?.payload as IVerifyOTPResponseData) || {};
      if (success) {
        // Login User
        saveUserToCookie({
          login_access_token: data?.access_token,
          purchase_token: data?.purchase_token,
          profile: data?.user,
          profile_country: country,
        });

        // after verifying OTP success, try to call to fetch profile again to get latest information
        try {
          const { slug: verifyOTPUserSlug, phone } = data?.user || {};
          // if the user profile phone same with the phone from buy now
          if (phone === userProfilePhone) {
            fetchProfile({ country: country || 'sg', slug: verifyOTPUserSlug });
          }
        } catch (error) {
          Logger.error('fetching user profile after buy now got error', error);
        }

        const userId = data?.user?.id?.toString();

        pushGTMEvent({
          event: 'user_login',
          user_id: userId,
        });

        // Call the action after verifyinng otp success
        onVerifyOTPSuccess(data?.access_token, userId);
      }
    } catch (error) {
      Logger.log(error);
    }
  };

  const handleChangeVerifyType = (type: string) => {
    setCurrentStep(0);
    const phone = phoneOTPState;
    resetOTPState();
    setType(type);
    setPhone(phone || null);

    handleRequestOTP(type);
  };

  const steps = [
    {
      title: 'shared.login.stepVerifyTitle',
      content: (
        <>
          <LoginStepOTP
            customContent={{ lockCountdown: 'shared.login.checkPhoneIsCorrectMY' }}
            country={country || 'sg'}
            maxNumberOfAttempts={LOGIN_OTP_MAX_ATTEMPTS}
            onVerifyOTP={handleVerifyOTP}
            onTryAnotherWay={nextStep}
            showAnotherWay={!lockSeconds && !skipRedirectAfterLockCountdown && !isCountdownOTP && !loading}
            onRequestOTP={handleRequestOTP}
            onResendOTP={handleResendOTP}
            onCountDownLockEnd={handleOnCountdownTimerEnd}
            isAnotherWayDisabled={!!(isCountdownOTP && countdownTimer)}
            skipRedirectAfterLockCountdown={skipRedirectAfterLockCountdown}
          />
          {lockSeconds || skipRedirectAfterLockCountdown ? (
            <StyledCustomButton onClick={onModalClose} variant="outline">
              {t('shared.login.closeAndTryAgain')}
            </StyledCustomButton>
          ) : null}
        </>
      ),
    },
    {
      title: 'shared.login.otherOptionsTitle',
      content: (
        <LoginStepAnotherType loading={loading} onFormSubmit={handleChangeVerifyType} country={country || 'sg'} />
      ),
    },
  ];

  // https://carrotech.atlassian.net/browse/CCV-1376
  // Show back button on the modal
  const customHeader = () => (
    <StyledCustomHeader className="login-custom-header">
      {currentStep > 0 && <LeftOutlined onClick={backStep} />}
      <div className={`modal-title-text ${screens.md ? 'modal-title' : 'drawer-title'}`}>
        {t(steps?.[currentStep]?.title)}
      </div>
      <StyledCloseIcon onClick={onModalClose} />
    </StyledCustomHeader>
  );

  const renderStepContent = () => (
    <div>
      {steps.map((step, index) => {
        const newKey = `login-step-${index + 1}`;

        // only render current step
        if (index === currentStep) {
          return (
            <div className={`step-${index}`} key={newKey}>
              {step?.content}
            </div>
          );
        }

        return null;
      })}
    </div>
  );

  const renderContent = <StyledContentWrapper>{renderStepContent()}</StyledContentWrapper>;

  return screens.md ? (
    <MobileModal
      customHeader={customHeader}
      visible={visible}
      hasFooter={false}
      hasFixHeight={false}
      hasStickyHeader={false}
      maskClosable
      onClose={onModalClose}
      closable={false}
      destroyOnClose
    >
      {renderContent}
    </MobileModal>
  ) : (
    <MobileDrawer
      customHeader={customHeader}
      visible={visible}
      customFooter={() => undefined}
      onClose={onModalClose}
      closable={false}
      destroyOnClose
    >
      {renderContent}
    </MobileDrawer>
  );
};

export default VerifyOTPContainer;
